import {useEffect, useId, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {X} from "lucide-react";
import {useTranslation} from "../i18n";
import "../finance/financeOperations.css";

const focusableElements = (container) =>
    Array.from(
        container.querySelectorAll(
            "button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex='-1'])",
        ),
    );

export const ConfirmationDialog = ({
    open,
    title,
    description,
    entity,
    confirmLabel,
    confirmationText,
    tone = "primary",
    pending = false,
    error = null,
    dialogKey,
    onCancel,
    onConfirm,
}) => {
    const {t} = useTranslation();
    const titleId = useId();
    const descriptionId = useId();
    const errorId = useId();
    const dialogRef = useRef(null);
    const confirmationRef = useRef(null);
    const submitRef = useRef(null);
    const invokingElementRef = useRef(null);
    const activeDialogIdentityRef = useRef(null);
    const [typedText, setTypedText] = useState("");
    const dialogIdentity = dialogKey ?? `${title || ""}:${entity || ""}`;
    const confirmed = !confirmationText || typedText === confirmationText;

    useEffect(() => {
        if (!open) return undefined;
        invokingElementRef.current = document.activeElement;
        const frame = window.requestAnimationFrame(() =>
            (confirmationRef.current || submitRef.current)?.focus(),
        );
        return () => {
            window.cancelAnimationFrame(frame);
            const invokingElement = invokingElementRef.current;
            if (invokingElement && document.contains(invokingElement)) invokingElement.focus();
            invokingElementRef.current = null;
        };
    }, [open]);

    useEffect(() => {
        if (!open) {
            activeDialogIdentityRef.current = null;
            return;
        }
        if (activeDialogIdentityRef.current === dialogIdentity) return;
        activeDialogIdentityRef.current = dialogIdentity;
        setTypedText("");
    }, [dialogIdentity, open]);

    useEffect(() => {
        if (!open) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                if (!pending) onCancel();
                return;
            }
            if (event.key !== "Tab" || !dialogRef.current) return;
            const focusable = focusableElements(dialogRef.current);
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onCancel, open, pending]);

    if (!open) return null;

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!pending && confirmed) onConfirm();
    };

    return createPortal(
        <div
            className="finance-dialog-backdrop"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !pending) onCancel();
            }}
        >
            <section
                ref={dialogRef}
                className="finance-dialog finance-confirmation-dialog"
                role="dialog"
                aria-modal="true"
                aria-busy={pending}
                aria-labelledby={titleId}
                aria-describedby={description ? descriptionId : undefined}
            >
                <form onSubmit={handleSubmit}>
                    <header className="finance-dialog-header">
                        <div>
                            {entity ? <p className="finance-dialog-entity">{entity}</p> : null}
                            <h2 id={titleId}>{title}</h2>
                        </div>
                        <button
                            className="finance-icon-button"
                            type="button"
                            aria-label={t("common.confirmation.close")}
                            title={t("common.confirmation.close")}
                            disabled={pending}
                            onClick={onCancel}
                        >
                            <X aria-hidden="true" size={18} />
                        </button>
                    </header>
                    {description ? <p id={descriptionId}>{description}</p> : null}
                    {confirmationText ? (
                        <label className="finance-confirmation-field">
                            <span>
                                {t("common.confirmation.typeToConfirm", {text: confirmationText})}
                            </span>
                            <input
                                ref={confirmationRef}
                                type="text"
                                value={typedText}
                                disabled={pending}
                                onChange={(event) => setTypedText(event.target.value)}
                            />
                        </label>
                    ) : null}
                    {error?.message ? (
                        <div id={errorId} className="finance-dialog-error" role="alert">
                            <p>{error.message}</p>
                        </div>
                    ) : null}
                    <footer className="finance-dialog-footer">
                        <button
                            type="button"
                            className="admin-button admin-button-secondary"
                            disabled={pending}
                            onClick={onCancel}
                        >
                            {t("common.confirmation.cancel")}
                        </button>
                        <button
                            ref={submitRef}
                            type="submit"
                            className={`admin-button ${tone === "destructive" ? "finance-confirmation-button-destructive" : "admin-button-primary"}`}
                            disabled={pending || !confirmed}
                        >
                            {pending
                                ? t("common.confirmation.pending")
                                : confirmLabel ?? t("common.confirmation.confirm")}
                        </button>
                    </footer>
                </form>
            </section>
        </div>,
        document.body,
    );
};
